import { InjectionToken, Provider } from '@angular/core';
import type { IRynReplayGridColumnDef } from './ryn-replay-grid-columns';

/**
 * One page that opts into the blocked-grid snapshot ladder (#31398).
 * Capture emits `{id}:snapshot` / `{id}:left`; the player reads columns from
 * the event (`columnsJson`), not from a hardcoded column list.
 */
export interface IRynReplayGridSnapshotPageConfig {
  /**
   * Stable page id. Becomes the AppEvent prefix:
   * `${id}:snapshot` and `${id}:left`.
   */
  id: string;
  /** Player panel title. */
  title: string;
  /**
   * Substring(s) that must appear in the playhead URL for the panel to show.
   * Hide-on-leave when the recording navigates elsewhere.
   */
  routeIncludes: string | readonly string[];
  /**
   * CSS selector for the page host (e.g. `ups-equipment-usage-page`).
   * When `pauseWhilePresent` is true, this is merged into presence-pause.
   */
  hostSelector: string;
  /** Presence-pause while the host is mounted. Default true. */
  pauseWhilePresent?: boolean;
  /** Focus-pause while focus is inside the host. Default false. */
  pauseOnFocus?: boolean;
  /** Meta property keys shown in the player header (order preserved). */
  metaKeys?: readonly string[];
  /**
   * Column keys whose cell values may be stored unmasked in the snapshot.
   * Default: none, so every cell value is masked. Add a column only when its
   * values are not sensitive (never amounts, pay, or personal data).
   */
  unmaskedColumns?: readonly string[];
  /** Optional row cap override for this page. */
  maxRows?: number;
  /**
   * Columns used when the Kendo grid is not mounted yet (early load).
   * Prefer live visible columns from the grid at capture time.
   */
  fallbackColumns?: readonly IRynReplayGridColumnDef[];
  /**
   * When set, only rows with a truthy value at this field are kept (e.g.
   * `EquipmentDispatchID` drops Kendo group-aggregate rows). Used by the
   * `rynReplayGridSnapshot` directive so pages need no includeRow callback.
   */
  requireField?: string;
}

/**
 * App-level registry of blocked-grid snapshot pages. Provided from the app
 * module (e.g. Sparta `AppModule`) so pages are configurable without forking
 * the recorder or player.
 */
export interface IRynReplayGridSnapshotsConfig {
  /** App token, e.g. `web-sparta` — must match `environment.rynReplay.app`. */
  app: string;
  pages: readonly IRynReplayGridSnapshotPageConfig[];
}

export const RYN_REPLAY_GRID_SNAPSHOTS_CONFIG =
  new InjectionToken<IRynReplayGridSnapshotsConfig>(
    'RYN_REPLAY_GRID_SNAPSHOTS_CONFIG'
  );

export const snapshotEventNameForPage = (pageId: string): string =>
  `${pageId}:snapshot`;

export const leaveEventNameForPage = (pageId: string): string =>
  `${pageId}:left`;

export const routeIncludesList = (
  routeIncludes: string | readonly string[]
): string[] =>
  (Array.isArray(routeIncludes) ? [...routeIncludes] : [routeIncludes]).filter(
    (s) => !!s
  );

/**
 * Host selectors that should presence-pause, derived from the app provider.
 */
export function presencePauseSelectorsFromGridSnapshots(
  config: IRynReplayGridSnapshotsConfig | null | undefined
): string[] {
  if (!config?.pages?.length) {
    return [];
  }
  return config.pages
    .filter((page) => page.pauseWhilePresent !== false)
    .map((page) => page.hostSelector)
    .filter((selector) => !!selector);
}

/**
 * Host selectors that should focus-pause, derived from the app provider.
 */
export function focusPauseSelectorsFromGridSnapshots(
  config: IRynReplayGridSnapshotsConfig | null | undefined
): string[] {
  if (!config?.pages?.length) {
    return [];
  }
  return config.pages
    .filter((page) => page.pauseOnFocus === true)
    .map((page) => page.hostSelector)
    .filter((selector) => !!selector);
}

/**
 * Drop into an app module next to `provideRynReplayRrwebBackend()`.
 *
 * Example:
 * ```ts
 * provideRynReplayGridSnapshots({
 *   app: 'web-sparta',
 *   pages: [{
 *     id: 'equipment-usage',
 *     title: 'Equipment Usage',
 *     routeIncludes: 'equipment-usage-page',
 *     hostSelector: 'ups-equipment-usage-page',
 *     pauseOnFocus: true,
 *     metaKeys: ['jobId', 'jobName', 'mode', 'dateStart', 'dateEnd'],
 *   }],
 * })
 * ```
 */
export const provideRynReplayGridSnapshots = (
  config: IRynReplayGridSnapshotsConfig
): Provider => ({
  provide: RYN_REPLAY_GRID_SNAPSHOTS_CONFIG,
  useValue: config,
});
